import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import Layout from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Settings, Mail, Bell, Send, AlertTriangle, Clock, DollarSign, Save, Users, Crown, User, Briefcase, Plus, Trash2, UserPlus, Copy, KeyRound, Sparkles, Eye, EyeOff, Check, Building2, Building, Upload, X, FolderTree, Shield, Globe, Lock, CalendarDays, RefreshCw, MessageSquare, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';

const SettingsPage = ({ user, onLogout }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'organization';
  const [activeTab, setActiveTab] = useState(initialTab);
  
  const [testEmail, setTestEmail] = useState(user?.email || '');
  const [sendingTest, setSendingTest] = useState(false);
  const [sendingSummary, setSendingSummary] = useState(false);
  const [checkingNotifications, setCheckingNotifications] = useState(false);
  const [lastNotificationResult, setLastNotificationResult] = useState(null);
  
  // Global settings
  const [defaultHourlyRate, setDefaultHourlyRate] = useState('120');
  const [sendTeamWelcomeEmails, setSendTeamWelcomeEmails] = useState(true);
  const [autoCalculateAllocation, setAutoCalculateAllocation] = useState(false);
  const [savingGlobalRate, setSavingGlobalRate] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(true);
  
  // Resource Planning Settings
  const [dailyWorkHours, setDailyWorkHours] = useState(8.5);
  const [projectWorkPercent, setProjectWorkPercent] = useState(60);
  const [availableProjectHours, setAvailableProjectHours] = useState(5.1);
  const [maxOverbookingPercent, setMaxOverbookingPercent] = useState(100);
  const [savingPlanningSettings, setSavingPlanningSettings] = useState(false);
  
  // LLM API Key settings
  const [llmApiKey, setLlmApiKey] = useState('');
  const [hasCustomKey, setHasCustomKey] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [savingApiKey, setSavingApiKey] = useState(false);
  
  // Perplexity API Key settings
  const [perplexityApiKey, setPerplexityApiKey] = useState('');
  const [hasPerplexityKey, setHasPerplexityKey] = useState(false);
  const [showPerplexityKey, setShowPerplexityKey] = useState(false);
  const [savingPerplexityKey, setSavingPerplexityKey] = useState(false);
  
  // Project-specific time tracking settings
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [hourlyRate, setHourlyRate] = useState('');
  const [savingRate, setSavingRate] = useState(false);

  // User management
  const [allUsers, setAllUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [updatingRole, setUpdatingRole] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  
  // Add user dialog
  const [showAddUserDialog, setShowAddUserDialog] = useState(false);
  const [newUserName, setNewUserName] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [newUserRole, setNewUserRole] = useState('user');

  // Organization settings
  const [organization, setOrganization] = useState(null);
  const [loadingOrg, setLoadingOrg] = useState(true);
  const [orgName, setOrgName] = useState('');
  const [savingOrg, setSavingOrg] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [addingUser, setAddingUser] = useState(false);
  
  // Department management
  const [departments, setDepartments] = useState([]);
  const [loadingDepts, setLoadingDepts] = useState(true);
  const [showDeptDialog, setShowDeptDialog] = useState(false);
  const [editingDept, setEditingDept] = useState(null);
  const [deptForm, setDeptForm] = useState({ name: '', description: '', visibility: 'department' });
  const [savingDept, setSavingDept] = useState(false);
  const [showDeptMembersDialog, setShowDeptMembersDialog] = useState(false);
  const [selectedDept, setSelectedDept] = useState(null);
  const [deptMembers, setDeptMembers] = useState([]);
  const [loadingDeptMembers, setLoadingDeptMembers] = useState(false);
  
  // User department assignment dialog
  const [showUserDeptDialog, setShowUserDeptDialog] = useState(false);
  const [selectedUserForDept, setSelectedUserForDept] = useState(null);
  const [selectedUserDepts, setSelectedUserDepts] = useState([]);
  const [savingUserDepts, setSavingUserDepts] = useState(false);
  
  // National Holidays
  const [holidays, setHolidays] = useState([]);
  const [loadingHolidays, setLoadingHolidays] = useState(false);
  const [showHolidayDialog, setShowHolidayDialog] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [holidayForm, setHolidayForm] = useState({ name: '', date: '', is_recurring: true, description: '' });
  const [savingHoliday, setSavingHoliday] = useState(false);
  const [deletingHoliday, setDeletingHoliday] = useState(null);
  const [holidayYear, setHolidayYear] = useState(new Date().getFullYear());
  const [importingHolidays, setImportingHolidays] = useState(false);
  const [holidayImportStatus, setHolidayImportStatus] = useState(null);
  
  // Invite user dialog
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteName, setInviteName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('user');
  const [invitingUser, setInvitingUser] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  
  // Reset password
  const [showResetPasswordDialog, setShowResetPasswordDialog] = useState(false);
  const [userToReset, setUserToReset] = useState(null);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [resetResult, setResetResult] = useState(null);

  // Admin or Department Admin can manage users (with different permissions)
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const isDepartmentAdmin = user?.role === 'department_admin';
  const canManageUsers = isAdmin || isDepartmentAdmin;

  useEffect(() => {
    fetchProjects();
    fetchAppSettings();
    fetchLlmKeyStatus();
    fetchPerplexityKeyStatus();
    fetchOrganization();
    fetchDepartments();
    fetchHolidays();
    if (canManageUsers) {
      fetchAllUsers();
    }
  }, [canManageUsers]);

  const fetchDepartments = async () => {
    try {
      setLoadingDepts(true);
      const response = await axios.get(`${API}/departments`);
      setDepartments(response.data);
    } catch (error) {
      console.error('Failed to fetch departments', error);
    } finally {
      setLoadingDepts(false);
    }
  };

  const handleCreateDepartment = async () => {
    if (!deptForm.name.trim()) {
      toast.error('Abteilungsname darf nicht leer sein');
      return;
    }
    
    setSavingDept(true);
    try {
      if (editingDept) {
        await axios.put(`${API}/departments/${editingDept.id}`, deptForm);
        toast.success('Abteilung aktualisiert');
      } else {
        await axios.post(`${API}/departments`, deptForm);
        toast.success('Abteilung erstellt');
      }
      setShowDeptDialog(false);
      setEditingDept(null);
      setDeptForm({ name: '', description: '', visibility: 'department' });
      fetchDepartments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSavingDept(false);
    }
  };

  const handleDeleteDepartment = async (deptId) => {
    if (!confirm('Möchten Sie diese Abteilung wirklich löschen? Projekte werden nicht gelöscht.')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/departments/${deptId}`);
      toast.success('Abteilung gelöscht');
      fetchDepartments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const openEditDepartment = (dept) => {
    setEditingDept(dept);
    setDeptForm({
      name: dept.name,
      description: dept.description || '',
      visibility: dept.visibility || 'department'
    });
    setShowDeptDialog(true);
  };

  const openDeptMembers = async (dept) => {
    setSelectedDept(dept);
    setShowDeptMembersDialog(true);
    setLoadingDeptMembers(true);
    
    try {
      const response = await axios.get(`${API}/departments/${dept.id}/members`);
      setDeptMembers(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Mitglieder');
    } finally {
      setLoadingDeptMembers(false);
    }
  };

  const handleAddUserToDepartment = async (userId) => {
    if (!selectedDept) return;
    
    try {
      await axios.post(`${API}/departments/${selectedDept.id}/members`, { user_id: userId });
      toast.success('Mitglied hinzugefügt');
      // Refresh members
      const response = await axios.get(`${API}/departments/${selectedDept.id}/members`);
      setDeptMembers(response.data);
      fetchDepartments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Hinzufügen');
    }
  };

  const handleRemoveUserFromDepartment = async (userId) => {
    if (!selectedDept) return;
    
    try {
      await axios.delete(`${API}/departments/${selectedDept.id}/members/${userId}`);
      toast.success('Mitglied entfernt');
      // Refresh members
      const response = await axios.get(`${API}/departments/${selectedDept.id}/members`);
      setDeptMembers(response.data);
      fetchDepartments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Entfernen');
    }
  };

  const handleToggleDeptAdmin = async (userId) => {
    if (!selectedDept) return;
    
    try {
      const response = await axios.put(`${API}/departments/${selectedDept.id}/members/${userId}/admin`);
      toast.success(response.data.message);
      // Refresh members
      const membersRes = await axios.get(`${API}/departments/${selectedDept.id}/members`);
      setDeptMembers(membersRes.data);
      fetchDepartments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Ändern');
    }
  };

  // User department assignment functions
  const openUserDeptDialog = (targetUser) => {
    setSelectedUserForDept(targetUser);
    setSelectedUserDepts(targetUser.department_ids || []);
    setShowUserDeptDialog(true);
  };

  const handleSaveUserDepts = async () => {
    if (!selectedUserForDept) return;
    
    setSavingUserDepts(true);
    try {
      await axios.put(`${API}/departments/user/${selectedUserForDept.id}/assignments`, {
        department_ids: selectedUserDepts
      });
      toast.success('Abteilungen aktualisiert');
      setShowUserDeptDialog(false);
      // Refresh users list
      fetchAllUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSavingUserDepts(false);
    }
  };

  const toggleUserDept = (deptId) => {
    setSelectedUserDepts(prev => 
      prev.includes(deptId) 
        ? prev.filter(id => id !== deptId)
        : [...prev, deptId]
    );
  };

  const fetchOrganization = async () => {
    try {
      setLoadingOrg(true);
      const response = await axios.get(`${API}/organizations/current`);
      setOrganization(response.data);
      setOrgName(response.data.name);
    } catch (error) {
      console.error('Failed to fetch organization', error);
    } finally {
      setLoadingOrg(false);
    }
  };

  const handleSaveOrganization = async () => {
    if (!orgName.trim()) {
      toast.error('Organisationsname darf nicht leer sein');
      return;
    }
    
    setSavingOrg(true);
    try {
      await axios.put(`${API}/organizations/current`, { name: orgName });
      toast.success('Organisation aktualisiert');
      setOrganization({ ...organization, name: orgName });
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSavingOrg(false);
    }
  };

  const handleUploadLogo = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
      toast.error('Nur PNG, JPEG oder WebP erlaubt');
      return;
    }
    
    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Datei zu groß (max 2MB)');
      return;
    }
    
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/organizations/current/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setOrganization({ ...organization, logo_url: response.data.logo_url });
      toast.success('Logo hochgeladen');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Hochladen');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleDeleteLogo = async () => {
    try {
      await axios.delete(`${API}/organizations/current/logo`);
      setOrganization({ ...organization, logo_url: null });
      toast.success('Logo entfernt');
    } catch (error) {
      toast.error('Fehler beim Entfernen des Logos');
    }
  };

  const fetchLlmKeyStatus = async () => {
    try {
      const response = await axios.get(`${API}/users/me/llm-key`);
      setHasCustomKey(response.data.has_custom_key);
    } catch (error) {
      console.error('Failed to fetch LLM key status', error);
    }
  };

  const handleSaveLlmKey = async () => {
    setSavingApiKey(true);
    try {
      await axios.put(`${API}/users/me/llm-key`, { llm_api_key: llmApiKey || null });
      toast.success(llmApiKey ? 'API-Key gespeichert!' : 'API-Key entfernt');
      setHasCustomKey(!!llmApiKey);
      setLlmApiKey('');
      setShowApiKey(false);
    } catch (error) {
      toast.error('Fehler beim Speichern des API-Keys');
    } finally {
      setSavingApiKey(false);
    }
  };

  const fetchPerplexityKeyStatus = async () => {
    try {
      const response = await axios.get(`${API}/users/me/perplexity-key`);
      setHasPerplexityKey(response.data.has_perplexity_key);
    } catch (error) {
      console.error('Failed to fetch Perplexity key status', error);
    }
  };

  const handleSavePerplexityKey = async () => {
    setSavingPerplexityKey(true);
    try {
      await axios.put(`${API}/users/me/perplexity-key`, { perplexity_api_key: perplexityApiKey || null });
      toast.success(perplexityApiKey ? 'Perplexity-Key gespeichert!' : 'Perplexity-Key entfernt');
      setHasPerplexityKey(!!perplexityApiKey);
      setPerplexityApiKey('');
      setShowPerplexityKey(false);
    } catch (error) {
      toast.error('Fehler beim Speichern des Perplexity-Keys');
    } finally {
      setSavingPerplexityKey(false);
    }
  };

  const fetchAllUsers = async () => {
    try {
      setLoadingUsers(true);
      const response = await axios.get(`${API}/users`);
      setAllUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    setUpdatingRole(userId);
    try {
      await axios.put(`${API}/users/${userId}/role`, { role: newRole });
      toast.success('Rolle erfolgreich aktualisiert');
      fetchAllUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Aktualisieren der Rolle');
    } finally {
      setUpdatingRole(null);
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    
    setDeletingUser(userToDelete.id);
    try {
      await axios.delete(`${API}/users/${userToDelete.id}`);
      toast.success(`${userToDelete.name} wurde gelöscht`);
      fetchAllUsers();
      setShowDeleteDialog(false);
      setUserToDelete(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen des Benutzers');
    } finally {
      setDeletingUser(null);
    }
  };

  const handleAddUser = async () => {
    if (!newUserName || !newUserEmail || !newUserPassword) {
      toast.error('Bitte füllen Sie alle Felder aus');
      return;
    }
    
    setAddingUser(true);
    try {
      await axios.post(`${API}/users`, {
        name: newUserName,
        email: newUserEmail,
        password: newUserPassword,
        role: newUserRole
      });
      toast.success(`${newUserName} wurde hinzugefügt`);
      fetchAllUsers();
      setShowAddUserDialog(false);
      resetAddUserForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen des Benutzers');
    } finally {
      setAddingUser(false);
    }
  };

  const resetAddUserForm = () => {
    setNewUserName('');
    setNewUserEmail('');
    setNewUserPassword('');
    setNewUserRole('user');
  };

  const handleInviteUser = async () => {
    if (!inviteName || !inviteEmail) {
      toast.error('Bitte füllen Sie Name und E-Mail aus');
      return;
    }
    
    setInvitingUser(true);
    try {
      const response = await axios.post(`${API}/users/invite`, {
        name: inviteName,
        email: inviteEmail,
        role: inviteRole
      });
      
      setInviteResult(response.data);
      
      if (response.data.email_sent) {
        toast.success(`Einladung an ${inviteEmail} gesendet`);
      } else {
        toast.info('Benutzer erstellt - Passwort muss manuell geteilt werden');
      }
      
      fetchAllUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Einladen des Benutzers');
    } finally {
      setInvitingUser(false);
    }
  };

  const resetInviteForm = () => {
    setInviteName('');
    setInviteEmail('');
    setInviteRole('user');
    setInviteResult(null);
  };

  const handleResetPassword = async () => {
    if (!userToReset) return;
    
    setResettingPassword(true);
    try {
      const response = await axios.post(`${API}/users/${userToReset.id}/reset-password`);
      setResetResult(response.data);
      
      if (response.data.email_sent) {
        toast.success(`Neues Passwort an ${userToReset.email} gesendet`);
      } else {
        toast.info('Passwort zurückgesetzt - muss manuell geteilt werden');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Zurücksetzen des Passworts');
    } finally {
      setResettingPassword(false);
    }
  };

  const resetPasswordDialog = () => {
    setUserToReset(null);
    setResetResult(null);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('In Zwischenablage kopiert');
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin':
        return <Crown className="w-4 h-4 text-yellow-600" />;
      case 'department_admin':
        return <Building className="w-4 h-4 text-purple-600" />;
      case 'projektleiter':
        return <Briefcase className="w-4 h-4 text-blue-600" />;
      default:
        return <User className="w-4 h-4 text-gray-600" />;
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin':
        return 'bg-yellow-500/20 text-yellow-600 dark:text-yellow-400 border-yellow-500/30';
      case 'department_admin':
        return 'bg-purple-500/20 text-purple-600 dark:text-purple-400 border-purple-500/30';
      case 'projektleiter':
        return 'bg-blue-500/20 text-blue-600 dark:text-blue-400 border-blue-500/30';
      default:
        return 'bg-gray-500/20 text-gray-600 dark:text-gray-400 border-gray-500/30';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'admin':
        return 'Administrator';
      case 'department_admin':
        return 'Abteilungsleiter';
      case 'projektleiter':
        return 'Projektleiter';
      default:
        return 'Benutzer';
    }
  };

  const fetchAppSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setDefaultHourlyRate(response.data.default_hourly_rate?.toString() || '120');
      setSendTeamWelcomeEmails(response.data.send_team_welcome_emails ?? true);
      setAutoCalculateAllocation(response.data.auto_calculate_allocation ?? false);
      setDailyWorkHours(response.data.daily_work_hours ?? 8.5);
      setProjectWorkPercent(response.data.project_work_percent ?? 60);
      setAvailableProjectHours(response.data.available_project_hours ?? 5.1);
      setMaxOverbookingPercent(response.data.max_overbooking_percent ?? 100);
    } catch (error) {
      console.error('Failed to fetch app settings', error);
    } finally {
      setLoadingSettings(false);
    }
  };

  const handleToggleWelcomeEmails = async (enabled) => {
    try {
      await axios.put(`${API}/settings`, { send_team_welcome_emails: enabled });
      setSendTeamWelcomeEmails(enabled);
      toast.success(enabled ? 'Willkommens-E-Mails aktiviert' : 'Willkommens-E-Mails deaktiviert');
    } catch (error) {
      toast.error('Fehler beim Speichern der Einstellung');
    }
  };

  const handleToggleAutoAllocation = async (enabled) => {
    try {
      await axios.put(`${API}/settings`, { auto_calculate_allocation: enabled });
      setAutoCalculateAllocation(enabled);
      toast.success(enabled 
        ? 'Automatische Allokationsberechnung aktiviert' 
        : 'Automatische Allokationsberechnung deaktiviert'
      );
    } catch (error) {
      toast.error('Fehler beim Speichern der Einstellung');
    }
  };

  const handleSavePlanningSettings = async () => {
    const hours = parseFloat(dailyWorkHours);
    const percent = parseInt(projectWorkPercent);
    const maxOverbooking = parseInt(maxOverbookingPercent);
    
    if (isNaN(hours) || hours < 1 || hours > 24) {
      toast.error('Bitte geben Sie gültige Arbeitsstunden ein (1-24)');
      return;
    }
    if (isNaN(percent) || percent < 10 || percent > 100) {
      toast.error('Bitte geben Sie einen gültigen Prozentsatz ein (10-100)');
      return;
    }
    if (isNaN(maxOverbooking) || maxOverbooking < 100 || maxOverbooking > 200) {
      toast.error('Bitte geben Sie einen gültigen Überbuchungs-Grenzwert ein (100-200)');
      return;
    }

    setSavingPlanningSettings(true);
    try {
      const response = await axios.put(`${API}/settings`, { 
        daily_work_hours: hours,
        project_work_percent: percent,
        max_overbooking_percent: maxOverbooking
      });
      setAvailableProjectHours(response.data.available_project_hours);
      toast.success(`Planungseinstellungen gespeichert: ${response.data.available_project_hours}h verfügbar pro Tag`);
    } catch (error) {
      toast.error('Fehler beim Speichern der Planungseinstellungen');
    } finally {
      setSavingPlanningSettings(false);
    }
  };

  const handleSaveGlobalRate = async () => {
    const rate = parseFloat(defaultHourlyRate);
    if (isNaN(rate) || rate < 0) {
      toast.error('Bitte geben Sie einen gültigen Stundensatz ein');
      return;
    }

    setSavingGlobalRate(true);
    try {
      await axios.put(`${API}/settings`, { default_hourly_rate: rate });
      toast.success(`Standard-Stundensatz auf CHF ${rate}/h gesetzt`);
    } catch (error) {
      toast.error('Fehler beim Speichern des Stundensatzes');
    } finally {
      setSavingGlobalRate(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to fetch projects', error);
    }
  };

  // Holidays CRUD
  const fetchHolidays = async (year = holidayYear) => {
    setLoadingHolidays(true);
    try {
      const response = await axios.get(`${API}/holidays/for-year/${year}`);
      setHolidays(response.data.holidays || []);
      // Check import status
      await checkHolidayImportStatus(year);
    } catch (error) {
      console.error('Failed to fetch holidays', error);
      toast.error('Fehler beim Laden der Feiertage');
    } finally {
      setLoadingHolidays(false);
    }
  };

  const checkHolidayImportStatus = async (year) => {
    try {
      // Check if any holiday absences exist for the year
      const response = await axios.get(`${API}/resource-planning/absences?year=${year}&type=holiday`);
      const holidayAbsences = response.data.filter(a => a.absence_type === 'holiday');
      setHolidayImportStatus(holidayAbsences.length > 0 ? 'imported' : 'not_imported');
    } catch (error) {
      setHolidayImportStatus('unknown');
    }
  };

  const handleImportHolidaysToAbsences = async () => {
    setImportingHolidays(true);
    try {
      const response = await axios.post(`${API}/holidays/import-to-absences/${holidayYear}`);
      toast.success(response.data.message);
      setHolidayImportStatus('imported');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Import');
    } finally {
      setImportingHolidays(false);
    }
  };

  const handleRemoveHolidaysFromAbsences = async () => {
    try {
      const response = await axios.delete(`${API}/holidays/remove-from-absences/${holidayYear}`);
      toast.success(response.data.message);
      setHolidayImportStatus('not_imported');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Entfernen');
    }
  };

  const handleSaveHoliday = async () => {
    if (!holidayForm.name || !holidayForm.date) {
      toast.error('Name und Datum sind erforderlich');
      return;
    }

    setSavingHoliday(true);
    try {
      // Format date correctly based on recurring flag
      let formattedDate = holidayForm.date;
      if (holidayForm.is_recurring && holidayForm.date.length === 10) {
        // Convert YYYY-MM-DD to MM-DD for recurring
        formattedDate = holidayForm.date.substring(5);
      }

      const payload = {
        name: holidayForm.name,
        date: formattedDate,
        is_recurring: holidayForm.is_recurring,
        description: holidayForm.description
      };

      if (editingHoliday) {
        await axios.put(`${API}/holidays/${editingHoliday.id}`, payload);
        toast.success('Feiertag aktualisiert');
      } else {
        await axios.post(`${API}/holidays`, payload);
        toast.success('Feiertag erstellt');
      }

      setShowHolidayDialog(false);
      setEditingHoliday(null);
      setHolidayForm({ name: '', date: '', is_recurring: true, description: '' });
      fetchHolidays(holidayYear);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSavingHoliday(false);
    }
  };

  const handleDeleteHoliday = async (holiday) => {
    setDeletingHoliday(holiday.id);
    try {
      await axios.delete(`${API}/holidays/${holiday.id}`);
      toast.success('Feiertag gelöscht');
      fetchHolidays(holidayYear);
    } catch (error) {
      toast.error('Fehler beim Löschen');
    } finally {
      setDeletingHoliday(null);
    }
  };

  const handleResetHolidaysToDefault = async () => {
    try {
      await axios.post(`${API}/holidays/reset-defaults`);
      toast.success('Feiertage auf Schweizer Standard zurückgesetzt');
      fetchHolidays(holidayYear);
    } catch (error) {
      toast.error('Fehler beim Zurücksetzen');
    }
  };

  const openEditHoliday = (holiday) => {
    setEditingHoliday(holiday);
    // Convert MM-DD to full date for the date picker
    let displayDate = holiday.date;
    if (holiday.is_recurring && holiday.date.length === 5) {
      displayDate = `${holidayYear}-${holiday.date}`;
    }
    setHolidayForm({
      name: holiday.name,
      date: displayDate,
      is_recurring: holiday.is_recurring,
      description: holiday.description || ''
    });
    setShowHolidayDialog(true);
  };

  const openNewHoliday = () => {
    setEditingHoliday(null);
    setHolidayForm({ name: '', date: '', is_recurring: true, description: '' });
    setShowHolidayDialog(true);
  };

  const handleProjectSelect = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    setSelectedProject(project);
    setHourlyRate(project?.hourly_rate?.toString() || '120');
  };

  const handleSaveHourlyRate = async () => {
    if (!selectedProject) {
      toast.error('Bitte wählen Sie ein Projekt aus');
      return;
    }
    
    const rate = parseFloat(hourlyRate);
    if (isNaN(rate) || rate < 0) {
      toast.error('Bitte geben Sie einen gültigen Stundensatz ein');
      return;
    }

    setSavingRate(true);
    try {
      await axios.put(`${API}/projects/${selectedProject.id}`, {
        hourly_rate: rate
      });
      toast.success(`Stundensatz für "${selectedProject.name}" auf CHF ${rate}/h gesetzt`);
      
      // Update local state
      setProjects(prev => prev.map(p => 
        p.id === selectedProject.id ? { ...p, hourly_rate: rate } : p
      ));
      setSelectedProject(prev => ({ ...prev, hourly_rate: rate }));
    } catch (error) {
      toast.error('Fehler beim Speichern des Stundensatzes');
    } finally {
      setSavingRate(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmail) {
      toast.error('Bitte geben Sie eine E-Mail-Adresse ein');
      return;
    }
    
    setSendingTest(true);
    try {
      const response = await axios.post(`${API}/email/test`, { email: testEmail });
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Senden der Test-E-Mail');
    } finally {
      setSendingTest(false);
    }
  };

  const handleSendSummary = async () => {
    setSendingSummary(true);
    try {
      const response = await axios.post(`${API}/email/summary`);
      toast.success(`Zusammenfassung gesendet an ${response.data.email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Senden der Zusammenfassung');
    } finally {
      setSendingSummary(false);
    }
  };

  const handleCheckNotifications = async () => {
    setCheckingNotifications(true);
    try {
      const response = await axios.post(`${API}/email/check-notifications`);
      setLastNotificationResult(response.data);
      if (response.data.overdue_tasks > 0 || response.data.critical_tasks > 0) {
        toast.success(`Benachrichtigungen gesendet: ${response.data.overdue_tasks} überfällig, ${response.data.critical_tasks} kritisch`);
      } else {
        toast.info('Keine überfälligen oder kritischen Tasks gefunden');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Prüfen der Benachrichtigungen');
    } finally {
      setCheckingNotifications(false);
    }
  };

  // Handle tab change and update URL
  const handleTabChange = (value) => {
    setActiveTab(value);
    setSearchParams({ tab: value });
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="settings-page">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Settings className="w-8 h-8" />
            Einstellungen
          </h1>
          <p className="text-muted-foreground">Verwalten Sie Ihre Anwendungseinstellungen</p>
        </div>

        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
          <TabsList className="grid grid-cols-6 w-full max-w-4xl">
            <TabsTrigger value="organization" className="flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              <span className="hidden sm:inline">Organisation</span>
            </TabsTrigger>
            <TabsTrigger value="users" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              <span className="hidden sm:inline">Benutzer</span>
            </TabsTrigger>
            <TabsTrigger value="ai" className="flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              <span className="hidden sm:inline">KI</span>
            </TabsTrigger>
            <TabsTrigger value="time" className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span className="hidden sm:inline">Zeit</span>
            </TabsTrigger>
            <TabsTrigger value="email" className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              <span className="hidden sm:inline">E-Mail</span>
            </TabsTrigger>
            <TabsTrigger value="feedback" className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              <span className="hidden sm:inline">Feedback</span>
            </TabsTrigger>
          </TabsList>

          {/* Tab: Organisation */}
          <TabsContent value="organization" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Organisation
                </CardTitle>
                <CardDescription>
                  Verwalten Sie Ihre Organisationseinstellungen
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {loadingOrg ? (
                  <div className="text-center py-8 text-muted-foreground">Laden...</div>
                ) : organization ? (
                  <>
                    {/* Organization Logo */}
                    <div className="space-y-3">
                      <Label>Logo</Label>
                      <div className="flex items-center gap-4">
                        <div className="w-24 h-24 rounded-lg border-2 border-dashed border-muted-foreground/25 flex items-center justify-center overflow-hidden bg-muted/50">
                          {organization.logo_url ? (
                            <img 
                              src={organization.logo_url} 
                              alt="Logo" 
                              className="w-full h-full object-contain"
                            />
                          ) : (
                            <Building2 className="w-10 h-10 text-muted-foreground/50" />
                          )}
                        </div>
                        <div className="space-y-2">
                          <div className="flex gap-2">
                            <label htmlFor="logo-upload">
                              <Button 
                                variant="outline" 
                                size="sm" 
                                disabled={uploadingLogo || !isAdmin}
                                asChild
                              >
                                <span className="cursor-pointer">
                                  <Upload className="w-4 h-4 mr-2" />
                                  {uploadingLogo ? 'Hochladen...' : 'Logo hochladen'}
                                </span>
                              </Button>
                            </label>
                            <input
                              id="logo-upload"
                              type="file"
                              accept="image/png,image/jpeg,image/webp"
                              className="hidden"
                              onChange={handleUploadLogo}
                              disabled={!isAdmin}
                            />
                            {organization.logo_url && isAdmin && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={handleDeleteLogo}
                                className="text-red-600 hover:text-red-700"
                              >
                                <X className="w-4 h-4 mr-1" />
                                Entfernen
                              </Button>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            PNG, JPEG oder WebP. Max 2MB. Wird auf Reports angezeigt.
                          </p>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    {/* Organization Name */}
                    <div className="space-y-3">
                      <Label htmlFor="org-name">Organisationsname</Label>
                      <div className="flex gap-2">
                        <Input
                          id="org-name"
                          value={orgName}
                          onChange={(e) => setOrgName(e.target.value)}
                          placeholder="Name Ihrer Organisation"
                          disabled={!isAdmin}
                          className="max-w-md"
                        />
                        {isAdmin && (
                          <Button 
                            onClick={handleSaveOrganization}
                            disabled={savingOrg || orgName === organization.name}
                          >
                            <Save className="w-4 h-4 mr-2" />
                            {savingOrg ? 'Speichern...' : 'Speichern'}
                          </Button>
                        )}
                      </div>
                    </div>

                    <Separator />

                    {/* Organization Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="p-4 rounded-lg bg-muted/50">
                        <div className="text-2xl font-bold">{organization.member_count || 0}</div>
                        <div className="text-sm text-muted-foreground">Mitglieder</div>
                      </div>
                      <div className="p-4 rounded-lg bg-muted/50">
                        <div className="text-2xl font-bold">{departments.length}</div>
                        <div className="text-sm text-muted-foreground">Abteilungen</div>
                      </div>
                      <div className="p-4 rounded-lg bg-muted/50">
                        <div className="text-2xl font-bold">{projects.length}</div>
                        <div className="text-sm text-muted-foreground">Projekte</div>
                      </div>
                      <div className="p-4 rounded-lg bg-muted/50">
                        <div className="text-sm text-muted-foreground">Ihre Rolle</div>
                        <Badge variant={user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'department_admin' ? 'default' : 'outline'}>
                          {user?.role === 'admin' ? 'Administrator' : 
                           user?.role === 'super_admin' ? 'Super Admin' :
                           user?.role === 'department_admin' ? 'Abteilungsleiter' :
                           user?.role === 'projektleiter' ? 'Projektleiter' : 'Benutzer'}
                        </Badge>
                      </div>
                    </div>

                    <Separator />

                    {/* Departments Section */}
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-lg font-semibold flex items-center gap-2">
                            <FolderTree className="w-5 h-5" />
                            Abteilungen
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            Verwalten Sie die Abteilungen Ihrer Organisation
                          </p>
                        </div>
                        {isAdmin && (
                          <Button onClick={() => {
                            setEditingDept(null);
                            setDeptForm({ name: '', description: '', visibility: 'department' });
                            setShowDeptDialog(true);
                          }}>
                            <Plus className="w-4 h-4 mr-2" />
                            Abteilung erstellen
                          </Button>
                        )}
                      </div>

                      {loadingDepts ? (
                        <div className="text-center py-4 text-muted-foreground">Laden...</div>
                      ) : departments.length === 0 ? (
                        <div className="text-center py-8 border rounded-lg border-dashed">
                          <FolderTree className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
                          <p className="text-muted-foreground">Noch keine Abteilungen vorhanden</p>
                          {isAdmin && (
                            <Button 
                              variant="outline" 
                              className="mt-3"
                              onClick={() => setShowDeptDialog(true)}
                            >
                              <Plus className="w-4 h-4 mr-2" />
                              Erste Abteilung erstellen
                            </Button>
                          )}
                        </div>
                      ) : (
                        <div className="grid gap-3">
                          {departments.map(dept => (
                            <div 
                              key={dept.id} 
                              className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-muted/30 transition-colors"
                            >
                              <div className="flex items-center gap-4">
                                <div className="p-2 rounded-lg bg-primary/10">
                                  <FolderTree className="w-5 h-5 text-primary" />
                                </div>
                                <div>
                                  <div className="font-medium flex items-center gap-2">
                                    {dept.name}
                                    {dept.visibility === 'organization' ? (
                                      <Badge variant="outline" className="text-xs">
                                        <Globe className="w-3 h-3 mr-1" />
                                        Org-weit
                                      </Badge>
                                    ) : (
                                      <Badge variant="outline" className="text-xs">
                                        <Lock className="w-3 h-3 mr-1" />
                                        Nur Abteilung
                                      </Badge>
                                    )}
                                  </div>
                                  {dept.description && (
                                    <p className="text-sm text-muted-foreground">{dept.description}</p>
                                  )}
                                  <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                                    <span>{dept.member_count} Mitglieder</span>
                                    <span>{dept.project_count} Projekte</span>
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => openDeptMembers(dept)}
                                >
                                  <Users className="w-4 h-4 mr-1" />
                                  Mitglieder
                                </Button>
                                {isAdmin && (
                                  <>
                                    <Button 
                                      variant="ghost" 
                                      size="sm"
                                      onClick={() => openEditDepartment(dept)}
                                    >
                                      Bearbeiten
                                    </Button>
                                    <Button 
                                      variant="ghost" 
                                      size="sm"
                                      className="text-red-600 hover:text-red-700"
                                      onClick={() => handleDeleteDepartment(dept.id)}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {!isAdmin && (
                      <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                        <p className="text-sm text-yellow-800">
                          Nur Administratoren können die Organisationseinstellungen bearbeiten.
                        </p>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    Keine Organisation gefunden
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Department Create/Edit Dialog */}
            <Dialog open={showDeptDialog} onOpenChange={setShowDeptDialog}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <FolderTree className="w-5 h-5" />
                    {editingDept ? 'Abteilung bearbeiten' : 'Neue Abteilung erstellen'}
                  </DialogTitle>
                  <DialogDescription>
                    {editingDept 
                      ? 'Bearbeiten Sie die Einstellungen dieser Abteilung.'
                      : 'Erstellen Sie eine neue Abteilung für Ihre Organisation.'
                    }
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="dept-name">Name *</Label>
                    <Input
                      id="dept-name"
                      placeholder="z.B. Marketing"
                      value={deptForm.name}
                      onChange={(e) => setDeptForm({ ...deptForm, name: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="dept-desc">Beschreibung</Label>
                    <Input
                      id="dept-desc"
                      placeholder="Optionale Beschreibung"
                      value={deptForm.description}
                      onChange={(e) => setDeptForm({ ...deptForm, description: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Projektsichtbarkeit</Label>
                    <Select 
                      value={deptForm.visibility} 
                      onValueChange={(v) => setDeptForm({ ...deptForm, visibility: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="department">
                          <div className="flex items-center gap-2">
                            <Lock className="w-4 h-4" />
                            Nur eigene Abteilung
                          </div>
                        </SelectItem>
                        <SelectItem value="organization">
                          <div className="flex items-center gap-2">
                            <Globe className="w-4 h-4" />
                            Alle Org-Projekte sichtbar
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Bestimmt, welche Projekte Mitglieder dieser Abteilung sehen können.
                    </p>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowDeptDialog(false)}>
                    Abbrechen
                  </Button>
                  <Button onClick={handleCreateDepartment} disabled={savingDept || !deptForm.name.trim()}>
                    {savingDept ? 'Speichern...' : (editingDept ? 'Speichern' : 'Erstellen')}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Department Members Dialog */}
            <Dialog open={showDeptMembersDialog} onOpenChange={setShowDeptMembersDialog}>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Mitglieder: {selectedDept?.name}
                  </DialogTitle>
                  <DialogDescription>
                    Verwalten Sie die Mitglieder dieser Abteilung.
                  </DialogDescription>
                </DialogHeader>
                <div className="py-4">
                  {loadingDeptMembers ? (
                    <div className="text-center py-4 text-muted-foreground">Laden...</div>
                  ) : deptMembers.length === 0 ? (
                    <div className="text-center py-6 text-muted-foreground">
                      <Users className="w-10 h-10 mx-auto mb-2 opacity-50" />
                      Keine Mitglieder in dieser Abteilung
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-[300px] overflow-y-auto">
                      {deptMembers.map(member => (
                        <div key={member.id} className="flex items-center justify-between p-3 rounded-lg border">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                              <User className="w-4 h-4 text-primary" />
                            </div>
                            <div>
                              <div className="font-medium flex items-center gap-2">
                                {member.name}
                                {member.is_department_admin && (
                                  <Badge variant="default" className="text-xs">
                                    <Shield className="w-3 h-3 mr-1" />
                                    Admin
                                  </Badge>
                                )}
                              </div>
                              <div className="text-xs text-muted-foreground">{member.email}</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            {isAdmin && (
                              <>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => handleToggleDeptAdmin(member.id)}
                                  title={member.is_department_admin ? 'Admin-Rechte entziehen' : 'Zum Admin machen'}
                                >
                                  <Shield className={`w-4 h-4 ${member.is_department_admin ? 'text-primary' : 'text-muted-foreground'}`} />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  className="text-red-600"
                                  onClick={() => handleRemoveUserFromDepartment(member.id)}
                                  title="Aus Abteilung entfernen"
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add member section */}
                  {canManageUsers && allUsers.length > 0 && (
                    <div className="mt-4 pt-4 border-t">
                      <Label className="text-sm font-medium">Mitglied hinzufügen</Label>
                      <Select onValueChange={handleAddUserToDepartment}>
                        <SelectTrigger className="mt-2">
                          <SelectValue placeholder="Benutzer auswählen..." />
                        </SelectTrigger>
                        <SelectContent>
                          {allUsers
                            .filter(u => !deptMembers.find(m => m.id === u.id))
                            .map(u => (
                              <SelectItem key={u.id} value={u.id}>
                                {u.name} ({u.email})
                              </SelectItem>
                            ))
                          }
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowDeptMembersDialog(false)}>
                    Schliessen
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Tab: Benutzerverwaltung */}
          <TabsContent value="users" className="space-y-6">
            {/* User Management Card - Visible to admins and department admins */}
            {canManageUsers ? (
              <Card data-testid="user-management-card">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Users className="w-5 h-5" />
                        Benutzerverwaltung
                        {isDepartmentAdmin && (
                          <Badge variant="outline" className="ml-2 text-xs">
                            Nur eigene Abteilung
                          </Badge>
                        )}
                      </CardTitle>
                      <CardDescription>
                        Verwalten Sie Benutzerrollen und Berechtigungen
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      {/* Add User Button */}
                      <Dialog open={showAddUserDialog} onOpenChange={(open) => { setShowAddUserDialog(open); if (!open) resetAddUserForm(); }}>
                        <DialogTrigger asChild>
                          <Button variant="outline" size="sm" data-testid="add-user-button">
                            <Plus className="w-4 h-4 mr-2" />
                            Hinzufügen
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Benutzer hinzufügen</DialogTitle>
                            <DialogDescription>
                              Erstellen Sie einen neuen Benutzer mit direktem Zugang
                            </DialogDescription>
                          </DialogHeader>
                          <div className="space-y-4 pt-4">
                            <div className="space-y-2">
                              <Label>Name</Label>
                              <Input
                                value={newUserName}
                                onChange={(e) => setNewUserName(e.target.value)}
                                placeholder="Max Mustermann"
                                data-testid="new-user-name"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>E-Mail</Label>
                              <Input
                                type="email"
                                value={newUserEmail}
                                onChange={(e) => setNewUserEmail(e.target.value)}
                                placeholder="max@beispiel.ch"
                                data-testid="new-user-email"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>Passwort</Label>
                          <Input
                            type="password"
                            value={newUserPassword}
                            onChange={(e) => setNewUserPassword(e.target.value)}
                            placeholder="••••••••"
                            data-testid="new-user-password"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Rolle</Label>
                          <Select value={newUserRole} onValueChange={setNewUserRole}>
                            <SelectTrigger data-testid="new-user-role">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="user">Benutzer</SelectItem>
                              <SelectItem value="projektleiter">Projektleiter</SelectItem>
                              <SelectItem value="admin">Administrator</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <Button onClick={handleAddUser} disabled={addingUser} className="w-full" data-testid="create-user-button">
                          {addingUser ? 'Wird erstellt...' : 'Benutzer erstellen'}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                  
                  {/* Invite User Button */}
                  <Dialog open={showInviteDialog} onOpenChange={(open) => { setShowInviteDialog(open); if (!open) resetInviteForm(); }}>
                    <DialogTrigger asChild>
                      <Button size="sm" data-testid="invite-user-button">
                        <UserPlus className="w-4 h-4 mr-2" />
                        Einladen
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Benutzer einladen</DialogTitle>
                        <DialogDescription>
                          Senden Sie eine Einladung per E-Mail
                        </DialogDescription>
                      </DialogHeader>
                      
                      {!inviteResult ? (
                        <div className="space-y-4 pt-4">
                          <div className="space-y-2">
                            <Label>Name</Label>
                            <Input
                              value={inviteName}
                              onChange={(e) => setInviteName(e.target.value)}
                              placeholder="Max Mustermann"
                              data-testid="invite-name"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>E-Mail</Label>
                            <Input
                              type="email"
                              value={inviteEmail}
                              onChange={(e) => setInviteEmail(e.target.value)}
                              placeholder="max@beispiel.ch"
                              data-testid="invite-email"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Rolle</Label>
                            <Select value={inviteRole} onValueChange={setInviteRole}>
                              <SelectTrigger data-testid="invite-role">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="user">Benutzer</SelectItem>
                                <SelectItem value="projektleiter">Projektleiter</SelectItem>
                                <SelectItem value="admin">Administrator</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <Button onClick={handleInviteUser} disabled={invitingUser} className="w-full" data-testid="send-invite-button">
                            <Mail className="w-4 h-4 mr-2" />
                            {invitingUser ? 'Wird gesendet...' : 'Einladung senden'}
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-4 pt-4">
                          <div className={`p-4 rounded-lg ${inviteResult.email_sent ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
                            <div className="font-medium mb-2">
                              {inviteResult.email_sent ? '✅ Einladung gesendet!' : '⚠️ Benutzer erstellt'}
                            </div>
                            <p className="text-sm text-muted-foreground mb-3">
                              {inviteResult.message}
                            </p>
                            
                            {inviteResult.temp_password && (
                              <div className="bg-white dark:bg-slate-800 p-3 rounded border dark:border-slate-700 mt-2">
                                <div className="text-xs text-muted-foreground mb-1">Temporäres Passwort:</div>
                                <div className="flex items-center gap-2">
                                  <code className="flex-1 bg-muted px-2 py-1 rounded text-sm font-mono">
                                    {inviteResult.temp_password}
                                  </code>
                                  <Button 
                                    variant="outline" 
                                    size="sm"
                                    onClick={() => copyToClipboard(inviteResult.temp_password)}
                                  >
                                    <Copy className="w-4 h-4" />
                                  </Button>
                                </div>
                                <p className="text-xs text-muted-foreground mt-2">
                                  Teilen Sie dieses Passwort sicher mit dem Benutzer
                                </p>
                              </div>
                            )}
                          </div>
                          
                          <Button 
                            variant="outline" 
                            onClick={resetInviteForm} 
                            className="w-full"
                          >
                            Weitere Person einladen
                          </Button>
                        </div>
                      )}
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Role Legend */}
                <div className="flex flex-wrap gap-4 p-4 bg-muted/50 rounded-lg text-sm">
                  <div className="flex items-center gap-2">
                    <Crown className="w-4 h-4 text-yellow-600" />
                    <span><strong>Administrator:</strong> Voller Zugriff, Rollenverwaltung</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Building className="w-4 h-4 text-purple-600" />
                    <span><strong>Abteilungsleiter:</strong> Abteilungsverwaltung, Benutzerzuweisung</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Briefcase className="w-4 h-4 text-blue-600" />
                    <span><strong>Projektleiter:</strong> Projektverantwortung, Teamübersicht</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-gray-600" />
                    <span><strong>Benutzer:</strong> Eigene Tasks und Zeiterfassung</span>
                  </div>
                </div>

                {/* Users List */}
                {loadingUsers ? (
                  <div className="text-center py-4 text-muted-foreground">Lade Benutzer...</div>
                ) : (
                  <div className="space-y-2">
                    {allUsers.map((u) => (
                      <div 
                        key={u.id} 
                        className={`flex items-center justify-between p-4 border rounded-lg ${
                          u.id === user.id ? 'bg-primary/5 border-primary/20' : 'bg-background'
                        }`}
                        data-testid={`user-row-${u.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium ${
                            u.role === 'admin' ? 'bg-yellow-500/20 text-yellow-600 dark:text-yellow-400' :
                            u.role === 'department_admin' ? 'bg-purple-500/20 text-purple-600 dark:text-purple-400' :
                            u.role === 'projektleiter' ? 'bg-blue-500/20 text-blue-600 dark:text-blue-400' :
                            'bg-gray-500/20 text-gray-600 dark:text-gray-400'
                          }`}>
                            {u.name?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                          <div>
                            <div className="font-medium flex items-center gap-2 text-foreground">
                              {u.name}
                              {u.id === user.id && (
                                <span className="text-xs px-2 py-0.5 bg-primary/10 text-primary rounded-full">Sie</span>
                              )}
                            </div>
                            <div className="text-sm text-muted-foreground">{u.email}</div>
                            {/* Department badges */}
                            {u.department_ids && u.department_ids.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {u.department_ids.map(deptId => {
                                  const dept = departments.find(d => d.id === deptId);
                                  return dept ? (
                                    <span key={deptId} className="text-xs px-2 py-0.5 bg-purple-500/20 text-purple-600 dark:text-purple-400 rounded-full flex items-center gap-1">
                                      <FolderTree className="w-3 h-3" />
                                      {dept.name}
                                    </span>
                                  ) : null;
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3">
                          {/* Current Role Badge */}
                          <span className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium border ${getRoleBadgeColor(u.role)}`}>
                            {getRoleIcon(u.role)}
                            {getRoleLabel(u.role)}
                          </span>
                          
                          {/* Department Assignment Button */}
                          {departments.length > 0 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-muted-foreground hover:text-purple-600"
                              onClick={() => openUserDeptDialog(u)}
                              title="Abteilungen zuweisen"
                              data-testid={`assign-dept-${u.id}`}
                            >
                              <FolderTree className="w-4 h-4" />
                            </Button>
                          )}
                          
                          {/* Role Selector */}
                          <Select
                            value={u.role || 'user'}
                            onValueChange={(value) => handleRoleChange(u.id, value)}
                            disabled={updatingRole === u.id}
                          >
                            <SelectTrigger className="w-[180px]" data-testid={`role-select-${u.id}`}>
                              <SelectValue placeholder="Rolle wählen" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="admin">
                                <div className="flex items-center gap-2">
                                  <Crown className="w-4 h-4 text-yellow-600" />
                                  Administrator
                                </div>
                              </SelectItem>
                              <SelectItem value="department_admin">
                                <div className="flex items-center gap-2">
                                  <Building className="w-4 h-4 text-purple-600" />
                                  Abteilungsleiter
                                </div>
                              </SelectItem>
                              <SelectItem value="projektleiter">
                                <div className="flex items-center gap-2">
                                  <Briefcase className="w-4 h-4 text-blue-600" />
                                  Projektleiter
                                </div>
                              </SelectItem>
                              <SelectItem value="user">
                                <div className="flex items-center gap-2">
                                  <User className="w-4 h-4 text-gray-600" />
                                  Benutzer
                                </div>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                          
                          {/* Reset Password Button - not for self */}
                          {u.id !== user.id && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-muted-foreground hover:text-primary"
                              onClick={() => { setUserToReset(u); setShowResetPasswordDialog(true); }}
                              title="Passwort zurücksetzen"
                              data-testid={`reset-password-${u.id}`}
                            >
                              <KeyRound className="w-4 h-4" />
                            </Button>
                          )}
                          
                          {/* Delete Button - not for self */}
                          {u.id !== user.id && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-muted-foreground hover:text-destructive"
                              onClick={() => { setUserToDelete(u); setShowDeleteDialog(true); }}
                              data-testid={`delete-user-${u.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="py-12 text-center">
              <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">Nur Administratoren können Benutzer verwalten</p>
            </CardContent>
          </Card>
        )}

        {/* Delete User Confirmation Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Benutzer löschen</DialogTitle>
              <DialogDescription>
                Sind Sie sicher, dass Sie <strong>{userToDelete?.name}</strong> ({userToDelete?.email}) löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
                Abbrechen
              </Button>
              <Button 
                variant="destructive" 
                onClick={handleDeleteUser}
                disabled={deletingUser === userToDelete?.id}
                data-testid="confirm-delete-user"
              >
                {deletingUser === userToDelete?.id ? 'Löschen...' : 'Löschen'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reset Password Dialog */}
        <Dialog open={showResetPasswordDialog} onOpenChange={(open) => { setShowResetPasswordDialog(open); if (!open) resetPasswordDialog(); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Passwort zurücksetzen</DialogTitle>
              <DialogDescription>
                {!resetResult 
                  ? `Setzen Sie das Passwort für ${userToReset?.name} (${userToReset?.email}) zurück.`
                  : 'Das Passwort wurde erfolgreich zurückgesetzt.'
                }
              </DialogDescription>
            </DialogHeader>
            
            {!resetResult ? (
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowResetPasswordDialog(false)}>
                  Abbrechen
                </Button>
                <Button 
                  onClick={handleResetPassword}
                  disabled={resettingPassword}
                  data-testid="confirm-reset-password"
                >
                  <KeyRound className="w-4 h-4 mr-2" />
                  {resettingPassword ? 'Wird zurückgesetzt...' : 'Passwort zurücksetzen'}
                </Button>
              </DialogFooter>
            ) : (
              <div className="space-y-4">
                <div className={`p-4 rounded-lg ${resetResult.email_sent ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
                  <div className="font-medium mb-2">
                    {resetResult.email_sent ? '✅ E-Mail gesendet!' : '⚠️ Passwort zurückgesetzt'}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {resetResult.message}
                  </p>
                  
                  {resetResult.temp_password && (
                    <div className="bg-white p-3 rounded border mt-3">
                      <div className="text-xs text-muted-foreground mb-1">Neues Passwort:</div>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 bg-muted px-2 py-1 rounded text-sm font-mono">
                          {resetResult.temp_password}
                        </code>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => copyToClipboard(resetResult.temp_password)}
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">
                        Teilen Sie dieses Passwort sicher mit dem Benutzer
                      </p>
                    </div>
                  )}
                </div>
                
                <Button 
                  variant="outline" 
                  onClick={() => setShowResetPasswordDialog(false)} 
                  className="w-full"
                >
                  Schliessen
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* User Department Assignment Dialog */}
        <Dialog open={showUserDeptDialog} onOpenChange={setShowUserDeptDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FolderTree className="w-5 h-5" />
                Abteilungen zuweisen
              </DialogTitle>
              <DialogDescription>
                {selectedUserForDept && (
                  <>Wählen Sie die Abteilungen für <strong>{selectedUserForDept.name}</strong></>
                )}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {departments.length === 0 ? (
                <p className="text-center text-muted-foreground py-4">
                  Keine Abteilungen vorhanden. Erstellen Sie zuerst eine Abteilung im "Organisation" Tab.
                </p>
              ) : (
                <div className="space-y-2">
                  {departments.map((dept) => (
                    <div 
                      key={dept.id}
                      className={`flex items-center justify-between p-3 border rounded-lg cursor-pointer transition-colors ${
                        selectedUserDepts.includes(dept.id) 
                          ? 'bg-purple-50 border-purple-300' 
                          : 'hover:bg-muted/50'
                      }`}
                      onClick={() => toggleUserDept(dept.id)}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          selectedUserDepts.includes(dept.id) ? 'bg-purple-200' : 'bg-muted'
                        }`}>
                          <FolderTree className={`w-4 h-4 ${selectedUserDepts.includes(dept.id) ? 'text-purple-700' : 'text-muted-foreground'}`} />
                        </div>
                        <div>
                          <div className="font-medium">{dept.name}</div>
                          {dept.description && (
                            <div className="text-xs text-muted-foreground">{dept.description}</div>
                          )}
                        </div>
                      </div>
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        selectedUserDepts.includes(dept.id) 
                          ? 'bg-purple-600 border-purple-600' 
                          : 'border-muted-foreground'
                      }`}>
                        {selectedUserDepts.includes(dept.id) && (
                          <Check className="w-3 h-3 text-white" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowUserDeptDialog(false)}>
                Abbrechen
              </Button>
              <Button onClick={handleSaveUserDepts} disabled={savingUserDepts}>
                {savingUserDepts ? 'Speichern...' : 'Speichern'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
          </TabsContent>

          {/* Tab: KI-Einstellungen */}
          <TabsContent value="ai" className="space-y-6">
            {/* KI-Einstellungen Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5" />
                  KI-Einstellungen
                </CardTitle>
                <CardDescription>
                  Konfigurieren Sie Ihren eigenen API-Key für die KI-Vorlagen-Generierung
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-muted/50 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="font-medium">OpenAI API-Key</p>
                      <p className="text-sm text-muted-foreground">
                        {hasCustomKey ? (
                          <span className="text-green-600 flex items-center gap-1">
                            <Check className="w-3 h-3" />
                            Eigener Key hinterlegt
                          </span>
                    ) : (
                      'Standard-Key wird verwendet'
                    )}
                  </p>
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="relative">
                  <Input
                    type={showApiKey ? 'text' : 'password'}
                    value={llmApiKey}
                    onChange={(e) => setLlmApiKey(e.target.value)}
                    placeholder={hasCustomKey ? '••••••••••••••••' : 'sk-...'}
                    className="pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full"
                    onClick={() => setShowApiKey(!showApiKey)}
                  >
                    {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
                
                <div className="flex gap-2">
                  <Button 
                    onClick={handleSaveLlmKey} 
                    disabled={savingApiKey}
                    className="flex-1"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {savingApiKey ? 'Speichern...' : 'Speichern'}
                  </Button>
                  {hasCustomKey && (
                    <Button 
                      variant="outline"
                      onClick={() => {
                        setLlmApiKey('');
                        handleSaveLlmKey();
                      }}
                      disabled={savingApiKey}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Entfernen
                    </Button>
                  )}
                </div>
                
                <p className="text-xs text-muted-foreground">
                  Sie können Ihren eigenen OpenAI-Key unter{' '}
                  <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-primary underline">
                    platform.openai.com
                  </a>
                  {' '}erstellen.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Perplexity API Key Card */}
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold">Perplexity API-Key</h3>
                  <p className="text-xs text-muted-foreground">Für das KI-Recherchetool mit echten Web-Suchen</p>
                </div>
                {hasPerplexityKey && (
                  <Badge variant="outline" className="text-green-600 border-green-600">
                    <Key className="w-3 h-3 mr-1" />
                    Eigener Key aktiv
                  </Badge>
                )}
              </div>
              
              <div className="space-y-3">
                <Label className="text-sm">Perplexity API-Key</Label>
                <div className="relative">
                  <Input
                    type={showPerplexityKey ? 'text' : 'password'}
                    value={perplexityApiKey}
                    onChange={(e) => setPerplexityApiKey(e.target.value)}
                    placeholder={hasPerplexityKey ? '••••••••••••••••' : 'pplx-...'}
                    className="pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full"
                    onClick={() => setShowPerplexityKey(!showPerplexityKey)}
                  >
                    {showPerplexityKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
                
                <div className="flex gap-2">
                  <Button 
                    onClick={handleSavePerplexityKey} 
                    disabled={savingPerplexityKey}
                    className="flex-1"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {savingPerplexityKey ? 'Speichern...' : 'Speichern'}
                  </Button>
                  {hasPerplexityKey && (
                    <Button 
                      variant="outline"
                      onClick={() => {
                        setPerplexityApiKey('');
                        handleSavePerplexityKey();
                      }}
                      disabled={savingPerplexityKey}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Entfernen
                    </Button>
                  )}
                </div>
                
                <p className="text-xs text-muted-foreground">
                  Sie können Ihren eigenen Perplexity-Key unter{' '}
                  <a href="https://www.perplexity.ai/settings/api" target="_blank" rel="noopener noreferrer" className="text-primary underline">
                    perplexity.ai/settings/api
                  </a>
                  {' '}erstellen. Mit diesem Key werden Ihre Recherchen direkt über Ihr Konto abgerechnet.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
          </TabsContent>

          {/* Tab: Zeiterfassung */}
          <TabsContent value="time" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Zeiterfassung
                </CardTitle>
                <CardDescription>
                  Standard-Stundensatz und projektspezifische Konfiguration
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Global Default Hourly Rate */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-primary" />
                <Label className="text-sm font-semibold">Standard-Stundensatz für neue Projekte</Label>
              </div>
              <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                  <div className="space-y-2 md:col-span-2">
                    <Label className="text-xs text-muted-foreground">Dieser Satz wird automatisch für alle neuen Projekte verwendet</Label>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-muted-foreground">CHF</span>
                      <Input
                        type="number"
                        min="0"
                        step="0.50"
                        value={defaultHourlyRate}
                        onChange={(e) => setDefaultHourlyRate(e.target.value)}
                        placeholder="z.B. 120"
                        className="max-w-[150px]"
                        disabled={loadingSettings}
                        data-testid="default-hourly-rate-input"
                      />
                      <span className="text-sm font-medium text-muted-foreground">/h</span>
                    </div>
                  </div>
                  <Button 
                    onClick={handleSaveGlobalRate} 
                    disabled={savingGlobalRate || loadingSettings}
                    className="w-full"
                    data-testid="save-default-rate-btn"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {savingGlobalRate ? 'Speichern...' : 'Speichern'}
                  </Button>
                </div>
              </div>
            </div>

            <Separator />

            {/* Auto-Calculate Allocation */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-primary" />
                <Label className="text-sm font-semibold">Ressourcenplanung</Label>
              </div>
              <div className="p-4 rounded-lg border bg-muted/30">
                <div className="flex items-center justify-between">
                  <div className="space-y-1 flex-1 mr-4">
                    <div className="font-medium">Automatische Allokationsberechnung</div>
                    <div className="text-sm text-muted-foreground">
                      Bei Task-Zuweisung wird die Ressourcen-Auslastung automatisch berechnet
                    </div>
                    <div className="text-xs text-muted-foreground mt-2 p-2 bg-background rounded border">
                      <strong>Berechnungslogik (pro Tag):</strong><br/>
                      • Jeder Task trägt täglich bei: (geplante Tage ÷ Task-Dauer) × 100%<br/>
                      • Beispiel: 5 Tage Arbeit über 5 Tage = 100% pro Tag<br/>
                      • Beispiel: 2 Tage Arbeit über 5 Tage = 40% pro Tag<br/>
                      • <span className="text-amber-600">Überlappen Tasks? Tageswerte addieren sich!</span><br/>
                      • Allokation = Durchschnitt aller Tageswerte
                    </div>
                  </div>
                  <Switch 
                    checked={autoCalculateAllocation} 
                    onCheckedChange={handleToggleAutoAllocation}
                    disabled={loadingSettings}
                    data-testid="auto-allocation-toggle"
                  />
                </div>
                <div className="mt-3 text-xs text-muted-foreground">
                  {autoCalculateAllocation 
                    ? "✓ Aktiv: Allokation wird bei jeder Task-Änderung neu berechnet"
                    : "○ Inaktiv: Neue Allokationen werden mit 50% Standard erstellt"
                  }
                </div>
              </div>

              {/* Capacity Planning Settings */}
              <div className="p-4 rounded-lg border bg-blue-50/50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800 mt-4">
                <div className="space-y-4">
                  <div>
                    <div className="font-medium text-blue-900 dark:text-blue-100">Kapazitätsplanung</div>
                    <div className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                      Definieren Sie, wie viel Prozent eines Arbeitstages für Projektarbeit verfügbar ist.
                    </div>
                    <div className="text-xs text-blue-600 dark:text-blue-400 mt-2 p-3 bg-white/50 dark:bg-black/20 rounded border border-blue-200 dark:border-blue-700">
                      <strong>Empfehlung aus der Praxis:</strong><br/>
                      • Typischer Arbeitstag: 8.5 Stunden<br/>
                      • Projektarbeit: 50-60% (4.0-5.0h) für definierte Tasks<br/>
                      • Rest: Emails, Support, Meetings, Admin-Aufgaben<br/>
                      <span className="text-blue-800 dark:text-blue-200">→ Diese Werte beeinflussen die Task-Dauer-Berechnung!</span>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                    <div className="space-y-2">
                      <Label className="text-xs text-blue-700 dark:text-blue-300">Tägliche Arbeitszeit</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min="1"
                          max="24"
                          step="0.5"
                          value={dailyWorkHours}
                          onChange={(e) => setDailyWorkHours(parseFloat(e.target.value) || 8.5)}
                          className="w-20"
                          disabled={loadingSettings}
                          data-testid="daily-work-hours-input"
                        />
                        <span className="text-sm text-muted-foreground">Stunden</span>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-xs text-blue-700 dark:text-blue-300">Projektarbeit</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min="10"
                          max="100"
                          step="5"
                          value={projectWorkPercent}
                          onChange={(e) => setProjectWorkPercent(parseInt(e.target.value) || 60)}
                          className="w-20"
                          disabled={loadingSettings}
                          data-testid="project-work-percent-input"
                        />
                        <span className="text-sm text-muted-foreground">%</span>
                      </div>
                    </div>
                    
                    {/* Max Overbooking Setting */}
                    <div className="space-y-1">
                      <Label className="text-xs text-orange-700 dark:text-orange-300">Max. Überbuchung erlaubt</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min="100"
                          max="200"
                          step="10"
                          value={maxOverbookingPercent}
                          onChange={(e) => setMaxOverbookingPercent(parseInt(e.target.value) || 100)}
                          className="w-20"
                          disabled={loadingSettings}
                          data-testid="max-overbooking-input"
                        />
                        <span className="text-sm text-muted-foreground">%</span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        100% = keine Überbuchung, 120% = 20% Puffer erlaubt
                      </p>
                    </div>
                    
                    <Button 
                      onClick={handleSavePlanningSettings} 
                      disabled={savingPlanningSettings || loadingSettings}
                      className="w-full"
                      data-testid="save-planning-settings-btn"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {savingPlanningSettings ? 'Speichern...' : 'Speichern'}
                    </Button>
                  </div>
                  
                  <div className="p-3 bg-green-50 dark:bg-green-950/30 rounded-lg border border-green-200 dark:border-green-800">
                    <div className="flex items-center gap-3">
                      <div className="text-2xl font-bold text-green-700 dark:text-green-300">
                        {availableProjectHours}h
                      </div>
                      <div className="text-sm text-green-600 dark:text-green-400">
                        verfügbar pro Tag für Projektarbeit
                      </div>
                    </div>
                    <div className="text-xs text-green-600 dark:text-green-400 mt-1">
                      ({dailyWorkHours}h × {projectWorkPercent}% = {(dailyWorkHours * projectWorkPercent / 100).toFixed(1)}h)
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <Separator />

            {/* Project-specific rates */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-muted-foreground" />
                <Label className="text-sm font-semibold">Projektspezifische Stundensätze</Label>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Projekt auswählen</Label>
                  <Select
                    value={selectedProject?.id || ''}
                    onValueChange={handleProjectSelect}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Projekt wählen..." />
                    </SelectTrigger>
                    <SelectContent>
                      {projects.map(project => (
                        <SelectItem key={project.id} value={project.id}>
                          {project.name} (CHF {project.hourly_rate || 120}/h)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Stundensatz (CHF/h)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.50"
                    value={hourlyRate}
                    onChange={(e) => setHourlyRate(e.target.value)}
                    placeholder="z.B. 120"
                    disabled={!selectedProject}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>&nbsp;</Label>
                  <Button 
                    onClick={handleSaveHourlyRate} 
                    disabled={!selectedProject || savingRate}
                    className="w-full"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {savingRate ? 'Speichern...' : 'Speichern'}
                  </Button>
                </div>
              </div>
              
              {selectedProject && (
                <div className="p-4 bg-muted/50 rounded-lg">
                  <div className="text-sm">
                    <strong>{selectedProject.name}</strong>
                    <div className="text-muted-foreground mt-1">
                      Aktueller Stundensatz: <span className="font-mono font-bold">CHF {selectedProject.hourly_rate || 120}/h</span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-2">
                      Der Stundensatz wird für die automatische Berechnung der effektiven Kosten aus der Zeiterfassung verwendet.
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Quick overview of all project rates */}
            {projects.length > 0 && (
              <>
                <Separator />
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Übersicht aller Stundensätze</Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {projects.map(project => (
                      <div 
                        key={project.id} 
                        className="flex items-center justify-between p-3 bg-background border rounded-lg text-sm cursor-pointer hover:bg-muted/50 transition-colors"
                        onClick={() => handleProjectSelect(project.id)}
                      >
                        <span className="truncate">{project.name}</span>
                        <span className="font-mono font-bold text-primary ml-2">
                          CHF {project.hourly_rate || 120}/h
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Nationale Feiertage Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <CalendarDays className="w-5 h-5" />
                  Nationale Feiertage
                </CardTitle>
                <CardDescription>
                  Zentrale Feiertage für alle Mitarbeiter. Diese Tage werden in der Ressourcenplanung berücksichtigt.
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                {/* Year Filter */}
                <Select 
                  value={holidayYear.toString()} 
                  onValueChange={(val) => {
                    setHolidayYear(parseInt(val));
                    fetchHolidays(parseInt(val));
                  }}
                >
                  <SelectTrigger className="w-24">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[2024, 2025, 2026, 2027, 2028, 2029, 2030].map(year => (
                      <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" onClick={handleResetHolidaysToDefault}>
                  <RefreshCw className="w-4 h-4 mr-1" />
                  CH Standard
                </Button>
                <Button size="sm" onClick={openNewHoliday}>
                  <Plus className="w-4 h-4 mr-1" />
                  Hinzufügen
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Import to Resource Planning Section */}
            <div className="mb-4 p-4 bg-muted/50 rounded-lg border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-sm">In Ressourcenplanung importieren</p>
                  <p className="text-xs text-muted-foreground">
                    Feiertage werden als Abwesenheiten für alle Mitarbeiter erfasst
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {holidayImportStatus === 'imported' ? (
                    <>
                      <Badge className="bg-green-100 text-green-700">
                        <Check className="w-3 h-3 mr-1" />
                        Importiert für {holidayYear}
                      </Badge>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={handleRemoveHolidaysFromAbsences}
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Entfernen
                      </Button>
                    </>
                  ) : (
                    <Button 
                      size="sm" 
                      onClick={handleImportHolidaysToAbsences}
                      disabled={importingHolidays || holidays.length === 0}
                    >
                      {importingHolidays ? (
                        <>
                          <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                          Importiere...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-1" />
                          Für {holidayYear} importieren
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {loadingHolidays ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : holidays.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <CalendarDays className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p>Keine Feiertage für {holidayYear} definiert</p>
                <Button variant="outline" className="mt-4" onClick={handleResetHolidaysToDefault}>
                  Schweizer Feiertage laden
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground mb-3">
                  {holidays.length} Feiertage für {holidayYear}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {holidays
                    .sort((a, b) => a.date.localeCompare(b.date))
                    .map(holiday => {
                      // Format display date from YYYY-MM-DD
                      let displayDate = holiday.date;
                      if (holiday.date.length === 10) {
                        const [year, month, day] = holiday.date.split('-');
                        displayDate = `${day}.${month}.`;
                      }
                      
                      return (
                        <div 
                          key={holiday.id}
                          className="flex items-center justify-between p-3 bg-background border rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-14 text-center">
                              <span className="font-mono text-sm font-medium">{displayDate}</span>
                            </div>
                            <div>
                              <p className="font-medium text-sm">{holiday.name}</p>
                              <div className="flex items-center gap-2">
                                {holiday.description && (
                                  <span className="text-xs text-muted-foreground">{holiday.description}</span>
                                )}
                                {holiday.is_recurring ? (
                                  <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">jährlich</span>
                                ) : (
                                  <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">einmalig</span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => openEditHoliday(holiday)}
                            >
                              <Settings className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDeleteHoliday(holiday)}
                              disabled={deletingHoliday === holiday.id}
                            >
                              {deletingHoliday === holiday.id ? (
                                <div className="animate-spin h-4 w-4 border-2 border-red-600 border-t-transparent rounded-full" />
                              ) : (
                                <Trash2 className="w-4 h-4" />
                              )}
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                </div>
                <p className="text-xs text-muted-foreground mt-4">
                  Hinweis: Jährlich wiederkehrende Feiertage werden automatisch für jedes Jahr angewendet. Einmalige Feiertage gelten nur für das angegebene Datum.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Holiday Add/Edit Dialog */}
        <Dialog open={showHolidayDialog} onOpenChange={setShowHolidayDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editingHoliday ? 'Feiertag bearbeiten' : 'Neuen Feiertag hinzufügen'}
              </DialogTitle>
              <DialogDescription>
                Definieren Sie einen nationalen Feiertag für alle Mitarbeiter.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="holidayName">Name *</Label>
                <Input
                  id="holidayName"
                  value={holidayForm.name}
                  onChange={(e) => setHolidayForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="z.B. Nationalfeiertag"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="holidayDate">Datum *</Label>
                <Input
                  id="holidayDate"
                  type="date"
                  value={holidayForm.date}
                  onChange={(e) => setHolidayForm(prev => ({ ...prev, date: e.target.value }))}
                />
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="isRecurring"
                  checked={holidayForm.is_recurring}
                  onCheckedChange={(checked) => setHolidayForm(prev => ({ ...prev, is_recurring: checked }))}
                />
                <Label htmlFor="isRecurring" className="text-sm">
                  Jährlich wiederkehrend (gleicher Tag/Monat jedes Jahr)
                </Label>
              </div>
              <div className="space-y-2">
                <Label htmlFor="holidayDesc">Beschreibung (optional)</Label>
                <Input
                  id="holidayDesc"
                  value={holidayForm.description}
                  onChange={(e) => setHolidayForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="z.B. Schweizer Bundesfeiertag"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowHolidayDialog(false)}>
                Abbrechen
              </Button>
              <Button onClick={handleSaveHoliday} disabled={savingHoliday}>
                {savingHoliday ? 'Speichern...' : (editingHoliday ? 'Aktualisieren' : 'Hinzufügen')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
          </TabsContent>

          {/* Tab: E-Mail-Benachrichtigungen */}
          <TabsContent value="email" className="space-y-6">
            {/* Email Notifications Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  E-Mail-Benachrichtigungen
                </CardTitle>
                <CardDescription>
                  Konfigurieren Sie, wann Sie E-Mail-Benachrichtigungen erhalten möchten
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Team Welcome Emails Toggle */}
                <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200 dark:bg-green-950/20 dark:border-green-800">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center">
                      <Users className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <div className="font-medium">Willkommens-E-Mails für Teammitglieder</div>
                  <div className="text-sm text-muted-foreground">
                    Neue Teammitglieder erhalten automatisch eine E-Mail mit allen Projektdetails
                  </div>
                </div>
              </div>
              <Switch 
                checked={sendTeamWelcomeEmails} 
                onCheckedChange={handleToggleWelcomeEmails}
                disabled={loadingSettings}
                data-testid="team-welcome-emails-toggle"
              />
            </div>

            <Separator />

            {/* Test Email Section */}
            <div className="space-y-4">
              <h3 className="font-semibold text-sm">Test-E-Mail senden</h3>
              <div className="flex gap-3">
                <div className="flex-1">
                  <Input
                    type="email"
                    placeholder="ihre@email.com"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                  />
                </div>
                <Button onClick={handleSendTestEmail} disabled={sendingTest}>
                  <Send className="w-4 h-4 mr-2" />
                  {sendingTest ? 'Senden...' : 'Test senden'}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Hinweis: Prüfen Sie auch Ihren Spam-Ordner, falls Sie keine Test-E-Mail erhalten.
              </p>
            </div>

            <Separator />

            {/* Notification Types */}
            <div className="space-y-4">
              <h3 className="font-semibold text-sm">Benachrichtigungstypen</h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-red-500/10 dark:bg-red-500/20 rounded-lg border border-red-500/30">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                      <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
                    </div>
                    <div>
                      <div className="font-medium text-foreground">Überfällige Tasks</div>
                      <div className="text-sm text-muted-foreground">E-Mail bei Tasks deren Fälligkeitsdatum überschritten ist</div>
                    </div>
                  </div>
                  <Switch defaultChecked />
                </div>

                <div className="flex items-center justify-between p-4 bg-orange-500/10 dark:bg-orange-500/20 rounded-lg border border-orange-500/30">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center">
                      <AlertTriangle className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                    </div>
                    <div>
                      <div className="font-medium text-foreground">Kritischer Pfad</div>
                      <div className="text-sm text-muted-foreground">E-Mail bei Tasks die das Projektende überschreiten</div>
                    </div>
                  </div>
                  <Switch defaultChecked />
                </div>

                <div className="flex items-center justify-between p-4 bg-blue-500/10 dark:bg-blue-500/20 rounded-lg border border-blue-500/30">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                      <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <div className="font-medium text-foreground">Wöchentliche Zusammenfassung</div>
                      <div className="text-sm text-muted-foreground">Jeden Montag eine Übersicht Ihrer Projekte</div>
                    </div>
                  </div>
                  <Switch defaultChecked />
                </div>
              </div>
            </div>

            <Separator />

            {/* Manual Actions */}
            <div className="space-y-4">
              <h3 className="font-semibold text-sm">Manuelle Aktionen</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Button 
                  variant="outline" 
                  onClick={handleSendSummary} 
                  disabled={sendingSummary}
                  className="h-auto py-4"
                >
                  <div className="flex flex-col items-center gap-2">
                    <Mail className="w-6 h-6" />
                    <span>{sendingSummary ? 'Wird gesendet...' : 'Zusammenfassung jetzt senden'}</span>
                  </div>
                </Button>

                <Button 
                  variant="outline" 
                  onClick={handleCheckNotifications} 
                  disabled={checkingNotifications}
                  className="h-auto py-4"
                >
                  <div className="flex flex-col items-center gap-2">
                    <Bell className="w-6 h-6" />
                    <span>{checkingNotifications ? 'Prüfe...' : 'Überfällige/Kritische prüfen'}</span>
                  </div>
                </Button>
              </div>

              {lastNotificationResult && (
                <div className="p-4 bg-muted rounded-lg text-sm">
                  <div className="font-medium mb-2">Letztes Ergebnis:</div>
                  <div className="flex gap-4">
                    <span className="text-red-600">{lastNotificationResult.overdue_tasks} überfällige Tasks</span>
                    <span className="text-orange-600">{lastNotificationResult.critical_tasks} kritische Tasks</span>
                  </div>
                  <div className="text-muted-foreground mt-1">Gesendet an: {lastNotificationResult.email}</div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
          </TabsContent>

          {/* Tab: Feedback */}
          <TabsContent value="feedback" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  Feedback & Änderungswünsche
                </CardTitle>
                <CardDescription>
                  Helfen Sie uns, Task2Plan zu verbessern
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30 rounded-xl p-6 border border-emerald-200 dark:border-emerald-800">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900 rounded-xl flex items-center justify-center flex-shrink-0">
                      <MessageSquare className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                        Ihre Meinung ist uns wichtig!
                      </h3>
                      <p className="text-slate-600 dark:text-slate-300 mb-4">
                        Haben Sie Verbesserungsvorschläge, Änderungswünsche oder möchten Sie uns Feedback geben? 
                        Wir freuen uns über jede Rückmeldung und arbeiten kontinuierlich daran, Task2Plan zu verbessern.
                      </p>
                      <Button 
                        onClick={() => window.open('https://task2plan.com/cr-form/2e7eaaebc560f404', '_blank')}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid="feedback-btn"
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Feedback-Formular öffnen
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
                    <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center mb-3">
                      <Sparkles className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <h4 className="font-semibold text-slate-900 dark:text-white mb-1">Feature-Wünsche</h4>
                    <p className="text-sm text-slate-500 dark:text-slate-400">Welche Funktionen fehlen Ihnen?</p>
                  </div>
                  <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
                    <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900 rounded-lg flex items-center justify-center mb-3">
                      <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                    </div>
                    <h4 className="font-semibold text-slate-900 dark:text-white mb-1">Probleme melden</h4>
                    <p className="text-sm text-slate-500 dark:text-slate-400">Haben Sie einen Bug gefunden?</p>
                  </div>
                  <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
                    <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900 rounded-lg flex items-center justify-center mb-3">
                      <MessageSquare className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                    </div>
                    <h4 className="font-semibold text-slate-900 dark:text-white mb-1">Allgemeines Feedback</h4>
                    <p className="text-sm text-slate-500 dark:text-slate-400">Teilen Sie Ihre Erfahrungen</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

        </Tabs>
      </div>
    </Layout>
  );
};

export default SettingsPage;
